/**
 * useVoteSubmit — Phase 2 voting hook
 *
 * Orchestrates the full vote submit flow for both source paths:
 * - Wallet path: optional approve() → vote()
 * - Staked path: voteWithStake() directly (no approve needed)
 *
 * Mirrors Phase 1 useBeCandidate orchestration style.
 *
 * AC-13 — wallet vote: approve check + vote
 * AC-14 — approve called only when allowance < amount
 * AC-17 — staked vote: voteWithStake only
 * AC-16, AC-35, EC-06, EC-07, EC-12 — error handling
 * AC-23 — invalidates 4 queryKeys on success (P2-02 r2: 4-segment keys)
 *
 * See: arandao-docs/pipeline/DAO-FOUND-02/idocs/voting/service-logic.md §2.4
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { toast } from "sonner";
import type { Address } from "viem";
import {
  useAccount,
  useChainId,
  usePublicClient,
  useSignMessage,
  useSwitchChain,
} from "wagmi";
import { EXPECTED_CHAIN_ID } from "@/configs/expected-chain";
import { ELECTION_ABI } from "@/contract/abi/election";
import { ERC20_ABI } from "@/contract/abi/erc20";
import ContractAddress from "@/contract/addresses";
import { useGuardedWriteContract } from "@/hooks/auth/useGuardedWrite";
import { useCurrentCycle } from "@/hooks/dao/useCurrentCycle";
import { useContractErrorHandler } from "@/hooks/useContractErrorHandler";
import { $api } from "@/lib/api-client";
import { useDnmSourceAvailability } from "./useDnmSourceAvailability";

/**
 * Canonical message format the user signs after a vote tx confirms.
 * MUST stay byte-for-byte identical to backend's buildVoteRecordMessage in
 * arandao-back/src/election/dao-vote.service.ts — otherwise signature
 * recovery yields a different address and the backend rejects.
 */
function buildVoteRecordMessage(input: {
  cycle: number;
  candidateAddress: string;
  amount: bigint;
  txHash: `0x${string}`;
  source: "wallet" | "stake";
}): string {
  return [
    "AranDAO — Record vote",
    `Cycle: ${input.cycle}`,
    `Candidate: ${input.candidateAddress.toLowerCase()}`,
    `Amount: ${input.amount.toString()} wei`,
    `Source: ${input.source}`,
    `Tx: ${input.txHash.toLowerCase()}`,
  ].join("\n");
}

export type VoteSource = "wallet" | "stake";

export interface VoteSubmitInput {
  source: VoteSource;
  amount: bigint; // wei
}

export interface VoteSubmitMutation {
  mutate: (input: VoteSubmitInput) => void;
  mutateAsync: (input: VoteSubmitInput) => Promise<{ txHash: `0x${string}` }>;
  isPending: boolean;
  isApprovePending: boolean;
  isVotePending: boolean;
  isSuccess: boolean;
  reset: () => void;
}

function isUserRejectionError(err: unknown): boolean {
  return (
    err instanceof Error && err.message.toLowerCase().includes("user rejected")
  );
}

export function useVoteSubmit(opts: {
  candidateAddress: Address;
}): VoteSubmitMutation {
  const t = useTranslations("dao");
  const { address: userAddress } = useAccount();
  const { handleContractError } = useContractErrorHandler();
  const { cycle } = useCurrentCycle();
  const liveChainId = useChainId();
  const { switchChainAsync } = useSwitchChain();
  const { writeContractAsync } = useGuardedWriteContract();
  const { signMessageAsync } = useSignMessage();
  const publicClient = usePublicClient();
  const queryClient = useQueryClient();

  // Read current allowance to determine if approve step is needed (AC-14)
  const { walletAllowance } = useDnmSourceAvailability(userAddress, cycle);

  // Backend recording mutation — POST /election/votes after a vote tx
  // confirms. Body carries the txHash + a personal_sign signature that the
  // backend recovers to verify it matches the JWT user's wallet. No RPC
  // calls in backend (per PM directive 2026-05-05).
  const recordVoteMutation = $api.useMutation("post", "/election/votes");

  const {
    mutate,
    mutateAsync: rawMutateAsync,
    isPending,
    isSuccess,
    reset,
  } = useMutation({
    mutationFn: async (
      input: VoteSubmitInput,
    ): Promise<{ txHash: `0x${string}` }> => {
      // Step 1: Ensure correct chain (D1)
      // B4 chain pre-flight — switchChainAsync may resolve "successfully"
      // without actually switching (wallet silent no-op). Verify the returned
      // Chain.id so the vote tx isn't fired on the wrong chain (wasting real
      // gas + producing unrecoverable state).
      if (liveChainId !== EXPECTED_CHAIN_ID) {
        const switched = await switchChainAsync({ chainId: EXPECTED_CHAIN_ID });
        if (switched.id !== EXPECTED_CHAIN_ID) {
          throw new Error(t("voting.toasts.chainSwitchFailed"));
        }
      }

      if (input.source === "wallet") {
        // Step 2a: Approve check (AC-13, AC-14) — skip if allowance is sufficient
        if (walletAllowance < input.amount) {
          const approveTxHash = await writeContractAsync({
            address: ContractAddress.DNM,
            abi: ERC20_ABI,
            functionName: "approve",
            args: [ContractAddress.ELECTION, input.amount],
          });
          if (publicClient) {
            await publicClient.waitForTransactionReceipt({
              hash: approveTxHash,
            });
          }
        }

        // Step 2b: Vote (AC-14)
        const voteTxHash = await writeContractAsync({
          address: ContractAddress.ELECTION,
          abi: ELECTION_ABI,
          functionName: "vote",
          args: [opts.candidateAddress, input.amount],
        });
        if (publicClient) {
          await publicClient.waitForTransactionReceipt({ hash: voteTxHash });
        }
        return { txHash: voteTxHash };
      }

      // Staked path: voteWithStake only, no approve (AC-17)
      const voteTxHash = await writeContractAsync({
        address: ContractAddress.ELECTION,
        abi: ELECTION_ABI,
        functionName: "voteWithStake",
        args: [opts.candidateAddress, input.amount],
      });
      if (publicClient) {
        await publicClient.waitForTransactionReceipt({ hash: voteTxHash });
      }
      return { txHash: voteTxHash };
    },

    onSuccess: async (data, input) => {
      // B1 fix — if cycle is undefined at this point, the on-chain vote
      // already happened but we'd silently skip recording it on the backend
      // (same failure mode as the original candidacy bug). Surface a toast
      // so the user knows their vote went through on-chain but their badge
      // won't appear; tell them to refresh. Do NOT throw — the vote IS valid.
      if (cycle === undefined) {
        toast.warning(t("voting.toasts.savePartial"));
      } else {
        // Record the per-candidate vote on the backend. The user's vote tx is
        // already on chain at this point — we now ask the wallet to sign a
        // canonical message describing what was voted, and POST that signature
        // along with the tx info as a single batched request. Backend recovers
        // the signer and verifies it matches the JWT user's wallet (no RPC).
        try {
          const message = buildVoteRecordMessage({
            cycle,
            candidateAddress: opts.candidateAddress,
            amount: input.amount,
            txHash: data.txHash,
            source: input.source,
          });
          const signature = await signMessageAsync({ message });
          await recordVoteMutation.mutateAsync({
            body: {
              txHash: data.txHash,
              candidateAddress: opts.candidateAddress.toLowerCase(),
              amount: input.amount.toString(),
              cycle,
              source: input.source,
              signature,
            },
          });
        } catch (err) {
          // Recording failure (user rejected sig, network error, etc.) must
          // NOT abort the vote — the chain mutation already succeeded and
          // the vote counts in `candidates[c][cycle]`. Surface a toast so
          // the user knows their badge may not appear immediately, but
          // don't bubble it up to the form / drawer success state.
          if (!isUserRejectionError(err)) {
            handleContractError(err);
          }
        }
      }

      // Invalidate wagmi's auto-generated read query keys so the candidate list
      // (vote counts) and source availability (DNM balance) refetch from chain.
      // wagmi v2 auto-keys all start with "readContracts" / "readContract" —
      // these prefix matches catch every dao voting hook + sibling reads.
      queryClient.invalidateQueries({ queryKey: ["readContracts"] });
      queryClient.invalidateQueries({ queryKey: ["readContract"] });
      // Invalidate the per-user vote aggregate so VotedDnmBadge updates and
      // VoteButton flips its label to "رأی دیگر" on the just-voted row.
      queryClient.invalidateQueries({
        queryKey: ["get", "/election/votes"],
      });
      // S1 fix — useFetchCandidates registers under openapi-react-query key
      // ["get", "/election", ...]. The legacy ["dao", "candidates"] key
      // matched no registered query so the backend display data (nicknames)
      // stayed stale after voting.
      queryClient.invalidateQueries({ queryKey: ["get", "/election"] });
    },

    onError: (err) => {
      // AC-16, AC-35, EC-06, EC-07 — silent for user rejection, decoded toast for all else
      if (!isUserRejectionError(err)) {
        handleContractError(err);
      }
    },
  });

  const mutateAsync = (
    input: VoteSubmitInput,
  ): Promise<{ txHash: `0x${string}` }> => rawMutateAsync(input);

  return {
    mutate,
    mutateAsync,
    isPending,
    // Phase 1 simplified: isApprovePending / isVotePending tracked at component level
    // if needed; hook exposes isPending for combined pending state
    isApprovePending: false,
    isVotePending: isPending,
    isSuccess,
    reset,
  };
}
